/**
 * Tiny shared store for the visitor's saved-property ids.
 *
 * The homepage, listing pages and property cards all render <PropertyCard/>,
 * so we need one truthy source that:
 *   - loads the current shortlist once (when authenticated)
 *   - lets every card read `hasSaved(id)` synchronously
 *   - toggles saved state via the API and notifies every subscriber
 *
 * Notifications are broadcast two ways:
 *   1. `subscribe(fn)` — for React components inside this bundle.
 *   2. `window` `CustomEvent('lh:saved-changed', { id, saved })` — for anything
 *       listening globally (e.g. the homepage Saved Homes rail).
 */
import { savedAPI } from '../services/api';

let savedIds = new Set();
let initPromise = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(savedIds); } catch { /* noop */ } });

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Sync check. Returns false when the store hasn't been populated yet. */
export const hasSaved = (id) => !!id && savedIds.has(id);

/** Number of saved properties currently known locally. */
export const savedCount = () => savedIds.size;

/**
 * Populate the store from `/api/saved`. Cheap re-entrancy — a second call
 * while the first is inflight just awaits the same promise. Pass
 * `isAuthenticated=false` to clear the store (e.g. after logout).
 */
export const initSaved = async (isAuthenticated) => {
  if (!isAuthenticated) {
    if (savedIds.size > 0) {
      savedIds = new Set();
      emit();
    }
    initPromise = null;
    return;
  }
  if (initPromise) return initPromise;
  initPromise = savedAPI
    .getAll()
    .then((props) => {
      const arr = Array.isArray(props) ? props : (props?.properties || []);
      savedIds = new Set(arr.map((p) => p._id || p.id).filter(Boolean));
      emit();
    })
    .catch(() => {
      // Silent — user probably isn't authed. Store stays empty.
    })
    .finally(() => {
      // Allow the next explicit refresh to re-hit the API.
      setTimeout(() => { initPromise = null; }, 200);
    });
  return initPromise;
};

/**
 * Toggle save state for a property. Optimistic — updates local set first,
 * calls the API in the background, and rolls back if the API rejects.
 *
 * Returns the new saved state (true = saved, false = unsaved).
 */
export const toggleSaved = async (id) => {
  if (!id) return false;
  const wasSaved = savedIds.has(id);
  const nextSaved = !wasSaved;
  if (nextSaved) savedIds.add(id); else savedIds.delete(id);
  emit();
  try {
    window.dispatchEvent(
      new CustomEvent('lh:saved-changed', { detail: { id, saved: nextSaved } }),
    );
  } catch { /* SSR / older browsers */ }
  try {
    if (nextSaved) await savedAPI.save(id);
    else await savedAPI.unsave(id);
    // Emit a second time once the API has confirmed the change so
    // downstream subscribers (e.g. the homepage Saved Homes rail that
    // re-fetches `/api/saved`) don't race the write and see stale data.
    emit();
  } catch (err) {
    // Roll back optimistic update on failure.
    if (nextSaved) savedIds.delete(id); else savedIds.add(id);
    emit();
    throw err;
  }
  return nextSaved;
};
